import React, { createContext, useContext, ReactNode } from 'react';
import { Permission } from '../types/permission.types';
import { hasPermission, hasAllPermissions, hasAnyPermissions } from '../utils/permission.util';

export interface User {
  _id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  permissions?: Permission[];
  [key: string]: any;
}

export interface PermissionContextType {
  user: User | null;
  permissions: Permission[];
  hasPermission: (menuPath: string | string[]) => boolean;
  hasAllPermissions: (menuPaths: string[]) => boolean;
  hasAnyPermissions: (menuPaths: string[]) => boolean;
  isLoading: boolean;
}

export interface PermissionProviderProps {
  user: User | null;
  isLoading?: boolean;
  children?: ReactNode;
}

export const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

/**
 * PermissionProvider - Provides permission context to the entire application
 *
 * @param user - Current authenticated user
 * @param isLoading - Whether the app is still loading
 * @param children - Child components
 */
export const PermissionProvider: React.FC<PermissionProviderProps> = ({
  user,
  isLoading = false,
  children,
}) => {
  const permissions = user?.permissions || [];

  const contextValue: PermissionContextType = {
    user: user || null,
    permissions,
    hasPermission: (menuPath: string | string[]) => {
      if (Array.isArray(menuPath)) {
        return hasAnyPermissions(menuPath, permissions);
      }
      return hasPermission(menuPath, permissions);
    },
    hasAllPermissions: (requiredPaths: string[]) =>
      hasAllPermissions(requiredPaths, permissions),
    hasAnyPermissions: (requiredPaths: string[]) =>
      hasAnyPermissions(requiredPaths, permissions),
    isLoading,
  };

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Hook to access the permission context
 *
 * @throws Error if used outside of PermissionProvider
 */
export const usePermissionContext = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error(
      'usePermissionContext must be used within PermissionProvider'
    );
  }
  return context;
};

/**
 * Hook to check if user has a specific permission (view permission for menu path)
 *
 * @param menuPath - Menu path or array of menu paths to check
 * @returns true if user has the permission
 */
export const useHasPermission = (menuPath: string | string[]): boolean => {
  const { hasPermission: check } = usePermissionContext();
  return check(menuPath);
};

/**
 * Hook to check if user has view permission for ALL specified menu paths
 *
 * @param menuPaths - Array of menu paths to check
 * @returns true if user has ALL permissions
 */
export const useHasAllPermissions = (menuPaths: string[]): boolean => {
  const { hasAllPermissions: check } = usePermissionContext();
  return check(menuPaths);
};

/**
 * Hook to check if user has view permission for ANY of the specified menu paths
 *
 * @param menuPaths - Array of menu paths to check
 * @returns true if user has ANY permission
 */
export const useHasAnyPermissions = (menuPaths: string[]): boolean => {
  const { hasAnyPermissions: check } = usePermissionContext();
  return check(menuPaths);
};
